import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import Dialog from '@rio-cloud/rio-uikit/lib/es/Dialog';
import Notification from '@rio-cloud/rio-uikit/Notification';
import SteppedProgressBar from '@rio-cloud/rio-uikit/SteppedProgressBar';
import CampaignDetailsForm from './CampaignDetailsForm';
import CampaignTargetDeliverableForm from './CampaignTargetDeliverableForm';
import CampaignTargetVehiclesForm from './CampaignTargetVehiclesForm';
import CampaignFormSummary from '~/features/campaigns/components/campaignForm/CampaignFormSummary';
import { useCreateCampaignMutation } from '~/api/campaigns/Campaign.api';
import { CreateCampaignFormValues } from '~/features/campaigns/models/CreateCampaignFormValues';
import { BundleDeliverableItem } from '~/api/models/bundle-deliverable-item';
import { toSemanticVersion } from '~/features/base/utils/versionNumberConverter';
import DeliverableTypeEnum = BundleDeliverableItem.DeliverableTypeEnum;

const DAFAULT_NUMBER_STAGES = 5;

interface CreateCampaignFormDialogProps {
    show: boolean;
    onHide: () => void;
    onSubmitSuccess: () => void;
    initialData?: CreateCampaignFormValues;
}

const CreateCampaignFormDialog: React.FC<CreateCampaignFormDialogProps> = ({
    show,
    onHide,
    onSubmitSuccess,
    initialData = {
        stageNumber: DAFAULT_NUMBER_STAGES,
    }
}) => {
    const intl = useIntl();

    const [createCampaign] = useCreateCampaignMutation();

    const [currentStep, setCurrentStep] = useState(0);

    const {
        handleSubmit,
        control,
        setValue,
        trigger,
        watch,
        reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            campaignName: initialData.campaignName || '',
            campaignManager: initialData.campaignManager || '',
            deviceType: initialData.deviceType,
            decisionBoardUrl: initialData.decisionBoardUrl || '',
            description: initialData.description || '',
            stageNumber: initialData.stageNumber || 0,
            deliverableType: initialData.deliverableType,
            deliverableId: initialData.deliverableId,
            deliverableVersion: initialData.deliverableVersion,
            targetGroups: initialData.targetGroups
        },
        mode: 'onChange'
    });

    const formValues = watch();

    React.useEffect(() => {
        if (!show) {
            reset({
                campaignName: initialData.campaignName || '',
                campaignManager: initialData.campaignManager || '',
                deviceType: initialData.deviceType,
                decisionBoardUrl: initialData.decisionBoardUrl || '',
                description: initialData.description || '',
                stageNumber: initialData.stageNumber || 0,
                deliverableType: initialData.deliverableType,
                deliverableId: initialData.deliverableId,
                deliverableVersion: initialData.deliverableVersion,
                targetGroups: initialData.targetGroups
            });
            setCurrentStep(0);
        }
    }, [show]);

    // Define the steps
    const steps = [
        {
            title: intl.formatMessage({ id: 'intl-msg:campaign:details' }),
            component: <CampaignDetailsForm control={control} />
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:campaign:targetDeliverable' }),
            component: <CampaignTargetDeliverableForm control={control} setValue={setValue} formValues={formValues}/>
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:campaign:targetVehicles' }),
            component: <CampaignTargetVehiclesForm control={control} setValue={setValue} />
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:review' }),
            component: <CampaignFormSummary formValues={formValues} />
        }
    ];

    const handleNext = async () => {
        // Validate the current step before proceeding
        const fieldsToValidate = [];

        // Define which fields to validate for each step
        if (currentStep === 0) {
            fieldsToValidate.push('campaignName', 'campaignManager', 'deviceType', 'decisionBoardUrl');
        } else if (currentStep === 1) {
            fieldsToValidate.push('deliverableType', 'deliverableId', 'deliverableVersion');
        } else if (currentStep === 2) {
            fieldsToValidate.push('targetGroups');
        }

        const isStepValid = await trigger(fieldsToValidate);

        if (isStepValid) {
            setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
        }
    };

    const handleBack = () => {
        setCurrentStep(prev => Math.max(prev - 1, 0));
    };

    const onSubmit = async(data: CreateCampaignFormValues) => {
        try {
            const body = {
                name: data.campaignName,
                manager: data.campaignManager,
                deviceType: data.deviceType,
                decisionBoard: data.decisionBoardUrl,
                description: data.description,
                vehicleGroup: data.targetGroups,
                status: 'DRAFT',
                progress: 0,
                deliverableId: data.deliverableId,
                deliverableType: data.deliverableType as DeliverableTypeEnum,
                deliverableVersion: toSemanticVersion(data.deliverableVersion),
                numberOfStages: data.stageNumber
            };
            await createCampaign(body).unwrap().then(() => {
                    onHide();
                    Notification.success(<FormattedMessage id='intl-msg:campaign:creation:toastSuccess'/>);
                    onSubmitSuccess();
                }
            );
        } catch (error) {
            console.error(error);
            Notification.error(
                <FormattedMessage
                    id='intl-msg:campaign:creation:toastFailure'
                    values={{ error: error?.message }}
                />
            );
        }
    };

    const title = <h4><FormattedMessage id='intl-msg:campaign:create'/></h4>;

    const body = (
        <div>
            <div style={{
                overflowX: 'hidden',
                transform: 'scale(0.7)',
                transformOrigin: 'left center',
                width: '143%',
                height: '90px',
                overflowY: 'hidden'
            }}>
                <SteppedProgressBar
                    variant='circle'
                    className={'margin-left-0 margin-right-0'}
                    selectedStepNumber={currentStep}
                    onSelectedChanged={(stepIndex) => setCurrentStep(stepIndex)}
                    mobileBreakpoint={false}
                    labels={[
                        { icon: '1', label: intl.formatMessage({ id: 'intl-msg:details' }) },
                        { icon: '2', label: intl.formatMessage({ id: 'intl-msg:deliverableLabel' }) },
                        { icon: '3', label: intl.formatMessage({ id: 'intl-msg:subModuleName.vehicles' }) },
                        { icon: '4', label: intl.formatMessage({ id: 'intl-msg:review' }) }
                    ]}
                />
            </div>

            <div>
                {steps[currentStep].component}
            </div>


        </div>
    );

    const footer = <div className='stepper-actions display-flex justify-content-end gap-20 margin-top-20'>
        <button
            type='button'
            className='btn btn-default'
            onClick={handleBack}
            disabled={currentStep === 0}
        >
            <FormattedMessage id='Back'/>
        </button>
        <button
            type='button'
            className='btn btn-primary'
            onClick={
                currentStep < steps.length - 1
                    ? handleNext
                    : handleSubmit(onSubmit)
            }
        >
            <FormattedMessage id={currentStep < steps.length - 1 ? 'Next' : 'Create Campaign'}/>
        </button>
    </div>;
    return (
        <Dialog
            className='create-campaign-dialog'
            show={show}
            showCloseButton={true}
            onHide={onHide}
            title={title}
            body={body}
            footer={footer}
            useOverflow
        />
    );
};

export default CreateCampaignFormDialog;
